import React from "react";
import aiImage from "../../../assets/cloudSolutions2.webp";
import strategyImage from "../../../assets/cloudSolutions1.webp";

export default function CloudSolutions() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] sm:h-[70vh] md:h-[800px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={aiImage}
            alt="Cloud Solutions"
            className="w-full h-full object-cover sm:object-contain md:object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            Cloud Solutions
          </h1>
          <p className="text-sm sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
            Migrate, modernize, and manage workloads across AWS, GCP, and Azure
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            {/* Image - Left Side */}
            <div className="flex-1 w-full">
              <img
                src={strategyImage}
                alt="Cloud Solutions"
                className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Content - Right Side */}
            <div className="flex-1 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-[#482A7A] text-center lg:text-left">
                Cloud Solutions
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6 text-center lg:text-left">
                Migrate, modernize, and manage workloads across AWS, GCP, and Azure using cost-effective, secure architectures.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed text-center lg:text-left">
                Our cloud expertise enables seamless migration and optimization of your infrastructure for enhanced scalability, security, and cost efficiency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Cloud Computing Works Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#482A7A]">
              How does cloud computing work?
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-8">
              Aside from understanding what a cloud solution is, you also have to know how it works in the first place.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              When you use a cloud-based solution, you're essentially renting space and resources on someone else's massive, interconnected network of servers. Here's how it all comes together:
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-300 hidden lg:block"></div>
            
            <div className="space-y-12">
              {/* Item 1 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-8 mb-6 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-600">
                    <h3 className="text-lg font-bold text-blue-600 mb-4">Data storage and management</h3>
                    <p className="text-sm text-gray-600">
                      Instead of saving data on your computer's hard drive or local server, it's stored in the cloud. This means you can access it from any internet-connected device. Think about when you use a web-based email service - you can access your emails from anywhere, right? That's a simple form of cloud computing.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block w-4 h-4 bg-blue-600 rounded-full relative z-10"></div>
                <div className="lg:w-1/2 lg:pl-8"></div>
              </div>
              
              {/* Item 2 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-8"></div>
                <div className="hidden lg:block w-4 h-4 bg-blue-600 rounded-full relative z-10"></div>
                <div className="lg:w-1/2 lg:pl-8 mb-6 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-lg border-r-4 border-green-600">
                    <h3 className="text-lg font-bold text-green-600 mb-4">Servers and virtualization</h3>
                    <p className="text-sm text-gray-600">
                      Cloud providers use a technology called virtualization. It separates physical servers into multiple, separate 'virtual' servers. This way, users can utilize more resources on demand without the need for physical expansion. It's like having a versatile, expandable office space that grows with your needs.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Item 3 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-8 mb-6 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-600">
                    <h3 className="text-lg font-bold text-purple-600 mb-4">Scalability</h3>
                    <p className="text-sm text-gray-600">
                      One of the biggest perks of understanding what a cloud solution is is scalability. As your business grows, you can easily increase your cloud capacity without the need to invest in physical infrastructure. It's like having a tap where you can control the flow of resources based on your current needs.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block w-4 h-4 bg-blue-600 rounded-full relative z-10"></div>
                <div className="lg:w-1/2 lg:pl-8"></div>
              </div>
              
              {/* Item 4 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-8"></div>
                <div className="hidden lg:block w-4 h-4 bg-blue-600 rounded-full relative z-10"></div>
                <div className="lg:w-1/2 lg:pl-8 mb-6 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-lg border-r-4 border-orange-600">
                    <h3 className="text-lg font-bold text-orange-600 mb-4">Software and applications</h3>
                    <p className="text-sm text-gray-600">
                      For software-as-a-service (SaaS) applications, the cloud hosts these applications and makes them available over the Internet. This setup frees you from installing, maintaining, and updating software on individual devices.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Item 5 */}
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-8 mb-6 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-600">
                    <h3 className="text-lg font-bold text-red-600 mb-4">Security and maintenance</h3>
                    <p className="text-sm text-gray-600">
                      What is a cloud-based solution? Well, cloud providers invest heavily in security measures. They manage the network's security, ensuring data safety and compliance with regulations. Additionally, they handle the maintenance and updates, ensuring the infrastructure's optimal performance.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block w-4 h-4 bg-blue-600 rounded-full relative z-10"></div>
                <div className="lg:w-1/2 lg:pl-8"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Use Cloud Computing Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#482A7A]">
              How to use cloud computing effectively?
            </h2>
            <h3 className="text-lg font-semibold mb-8 text-gray-700">
              Smartest ways to use cloud computing model in your business
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-4xl mx-auto">
              Knowing the answer to the question of what a cloud solution is is as important as understanding its benefits. Here's how you can use cloud computing in the best ways with straightforward, actionable steps:
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8">
            <div className="relative bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Choose the right service model:</h4>
              <p className="text-gray-600 text-sm">Depending on your needs, pick from Software-as-a-Service (SaaS) for web-based applications, Infrastructure-as-a-Service (IaaS) for internet-based access to storage and computing resources, or Platform-as-a-Service (PaaS) for developers looking to create applications over the internet.</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Ensure proper integration:</h4>
              <p className="text-gray-600 text-sm">Seamlessly integrate cloud services with your existing IT infrastructure. This may involve syncing your on-premises data with the cloud or adopting cloud-native applications.</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Focus on security and compliance:</h4>
              <p className="text-gray-600 text-sm">Adopt robust security measures like encryption and identity management. Also, ensure your cloud services comply with relevant regulations and standards to protect sensitive data.</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Implement effective data management:</h4>
              <p className="text-gray-600 text-sm">Aside from knowing what a cloud solution is, you have to know how to organize and manage your data effectively in the cloud. Use cloud tools for data analysis and insights to drive better business decisions.</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Plan for scalability:</h4>
              <p className="text-gray-600 text-sm">Leverage the scalability of cloud computing to grow and shrink resources according to your business needs, ensuring cost-effectiveness and efficiency.</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">6</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Regular training and updates:</h4>
              <p className="text-gray-600 text-sm">Keep your team trained on the latest cloud technologies and updates. This ensures everyone can use cloud resources effectively and securely.</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm">7</div>
              <h4 className="font-semibold text-gray-800 mb-3 mt-2">Leverage automation:</h4>
              <p className="text-gray-600 text-sm">Automate repetitive tasks like data backups, security checks, and system updates to save time and reduce human error.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cloud Computing Examples Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#482A7A]">
              What is a cloud based solution: Best examples in cloud computing
            </h2>
            <h3 className="text-lg font-semibold mb-8 text-gray-700">
              7 different types of cloud computing and the best examples of each cloud service
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-4xl mx-auto">
              Understanding what a cloud solution is is key in today's digitally-driven business world. Let's dive into the different types of cloud-based solutions and some top examples in each category.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-blue-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-blue-600 text-white p-4">
                <h3 className="text-lg font-bold">1. Software as a Service (SaaS)</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  SaaS is like having a powerful software tool at your fingertips without the need for installation or maintenance, all accessed via the internet. It's a flexible, cost-effective solution, especially for businesses looking to minimize IT complexities.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Salesforce</strong> is a premier SaaS platform offering a diverse range of CRM functionalities. From tracking sales leads to managing customer interactions and analyzing marketing campaigns, Salesforce streamlines these processes in a cloud environment, making them accessible and efficient for businesses of all sizes.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-green-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-green-600 text-white p-4">
                <h3 className="text-lg font-bold">2. Infrastructure as a Service (IaaS)</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  What is a cloud-based solution for physical resources? IaaS provides the essential computing infrastructure that businesses need: servers, storage, and networking resources. It's like renting the physical infrastructure of IT, but over the internet, giving you the freedom to scale up or down as needed.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Amazon Web Services</strong> is a leader in IaaS, offering scalable computing power, extensive storage options, and robust networking capabilities. Whether you're a startup or a large enterprise, AWS provides the necessary infrastructure to support your business's growth and technological demands.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-purple-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-purple-600 text-white p-4">
                <h3 className="text-lg font-bold">3. Platform as a Service (PaaS)</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  PaaS offers a cloud-based platform that allows you to develop, test, run, and manage applications without worrying about the underlying infrastructure. It's ideal for developers who want to focus on building software without the overhead of maintaining servers and networks.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Microsoft Azure</strong> exemplifies PaaS, providing a wide array of tools for application development and management. From building mobile apps to supporting large-scale enterprise applications, Azure offers a versatile and robust environment for developers.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-teal-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-teal-600 text-white p-4">
                <h3 className="text-lg font-bold">4. File sharing and data storage</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  Now, what is a cloud-based solution that involves files? File-sharing and data storage solutions provide a secure and accessible way to store and share files across devices and locations. This flexibility is crucial for collaboration in today's mobile and distributed work environments.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Dropbox</strong> is a widely used service for cloud-based storage and file sharing. It simplifies storing and accessing documents, photos, and videos, ensuring that your files are always within reach, whether you're working from the office, home, or on the go.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-orange-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-orange-600 text-white p-4">
                <h3 className="text-lg font-bold">5. Big data analysis</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  Big data analysis in the cloud allows businesses to process and analyze large volumes of data efficiently. This capability is essential for gaining insights and making informed decisions in a data-driven world.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Google Cloud's BigQuery</strong> stands out for its ability to perform rapid, complex queries on massive datasets. It harnesses Google's processing power to deliver real-time analytics, enabling businesses to extract valuable insights from their data quickly.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-indigo-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-indigo-600 text-white p-4">
                <h3 className="text-lg font-bold">6. Data governance</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  Data governance in the cloud involves managing and regulating data access, ensuring data integrity, and complying with data protection regulations. It's about having a clear framework for handling data across its lifecycle.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>IBM Cloud Pak for Data</strong> provides an integrated data governance solution that helps you collect, organize, and analyze your data. It enables businesses to infuse AI across their operations while maintaining data security and compliance.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4 border-red-600 w-full lg:w-[calc(50%-16px)]">
              <div className="bg-red-600 text-white p-4">
                <h3 className="text-lg font-bold">7. Cybersecurity</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4 text-sm">
                  What is a cloud based solution for IT security? In an era where data breaches are increasingly common, cloud-based cybersecurity solutions are critical for protecting sensitive data and ensuring business continuity.
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Norton</strong> offers comprehensive protection against various online threats, including viruses, malware, and cyber-attacks. It's a crucial tool for safeguarding your business's and customers' data in the cloud.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}